import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Row, Col, Form, Button } from "react-bootstrap";
import Constants from "../../../../common/Constants";
import moment from "moment";
import { formatDate, formatDateTime } from "../../../../common/Validations";
import { useSelector } from "react-redux";
import ItemService from "../../../../services/commonService";
import { toastFunc } from "../../../common/util";
import AckRecords from "./AckRecords";
import Card from "react-bootstrap/Card";
import DataTable from "react-data-table-component";
import { DropdownListField } from "../../../../common/formElements/DropdownListField";
import DatePicker from "react-widgets/DatePicker";

const DeptAckStatusReports = () => {
  const { t } = useTranslation();

  const [jobcard] = useState("");
  const [ackData, setAckData] = useState([]);
  const [ackModel, setAckModal] = useState(false);

  const [allDepartmentMaster, setAllDepartmentMaster] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [requiredFrom, setRequiredFrom] = useState(new Date());

  const [rowsData, setRowsData] = useState([]);
  const [loaderStatus, setLoaderStatus] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [monthlyLoaderStatus, setMonthlyLoaderStatus] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [monthlyData, setMonthlyData] = useState({});

  const getAllDepartmentMaster = useSelector(
    (state) => state?.common?.departmentMaster,
  );

  useEffect(() => {
    const data = getAllDepartmentMaster || [];
    setAllDepartmentMaster(data);

    if (data.length > 0) {
      setSelectedDepartment((prev) => prev || data[0]);
    }
  }, [getAllDepartmentMaster]);

  useEffect(() => {
    if (selectedDepartment && requiredFrom) {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDepartment]);

  const handleDepartmentChange = (value) => {
    setSelectedDepartment(value || null);
  };

  const handleDateChange = (date) => {
    if (!date) {
      setRequiredFrom(new Date());
      return;
    }

    const selectedDate = new Date(date);
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    if (selectedDate > today) {
      toastFunc(
        Constants.MASTER.TOAST.error,
        "Future date is not allowed",
        3000,
      );
      return;
    }

    setRequiredFrom(selectedDate);
  };

  const handleAckModelClose = () => {
    setAckData([]);
    setAckModal(false);
  };

  const handleAckRecords = async (row) => {
    const param = {
      url: "getAckRecordsBasedOnJobcard",
      page: Constants.TRANSACTION.ACKNOLEDGEJOBCARD.name,
      jobcard: row.jobcard,
    };

    try {
      const item = await ItemService.getAckRecordsBasedOnJobcard(param);
      if (item?.status === 200 && item?.data?.length > 0) {
        setAckData(item.data);
        setAckModal(true);
      } else {
        setAckData([]);
        toastFunc(
          Constants.MASTER.TOAST.error,
          "No acknowledgement records found",
          3000,
        );
      }
    } catch (error) {
      console.error("Error in handleAckRecords:", error);
      toastFunc(
        Constants.MASTER.TOAST.error,
        t("msg_trasaction.msg_teamlead_acknowledge.error_occurred"),
        4000,
      );
    }
  };

  const handleSearch = async () => {
    if (!selectedDepartment || !requiredFrom) {
      toastFunc(
        Constants.MASTER.TOAST.error,
        "Please select Department and Date",
        3000,
      );
      return;
    }

    setMonthlyData({});
    setLoaderStatus(true);
    setMonthlyLoaderStatus(true);

    const obj = {
      deptCode: selectedDepartment?.id,
      currentDate: moment(requiredFrom).format("DD-MM-YYYY"),
    };

    try {
      const item = await ItemService.getdeptAckStatusReports(obj);

      if (item?.status === 200) {
        setRowsData(item?.data || []);
      } else {
        setRowsData([]);
        toastFunc(
          Constants.MASTER.TOAST.error,
          item?.message || "Unable to fetch report data",
          3000,
        );
      }
    } catch (error) {
      console.error("Error in handleSearch:", error);
      setRowsData([]);
      toastFunc(Constants.MASTER.TOAST.error, "Error fetching data", 3000);
    } finally {
      setLoaderStatus(false);
      setMonthlyLoaderStatus(false);
    }
  };

  const columnsData = [
    {
      name: t("msg_reports.msg_dept_ack_status_report.col_jobcard"),
      selector: (row) => row.jobcard,
      sortable: true,
      width: "180px",
      cell: (row) => (
        <span className="bg-table-status bg-table-new-status">
          {row.jobcard}
        </span>
      ),
      conditionalCellStyles: [
        {
          when: () => true,
          style: {
            position: "sticky",
            left: "0px",
            background: "#fff",
            zIndex: 2,
            boxShadow: "2px 0 5px -2px #ddd",
          },
        },
      ],
    },
    {
      name: "Ack Details",
      cell: (row) => (
        <div style={{ width: "100%", textAlign: "center" }}>
          <button className="btnTable" onClick={() => handleAckRecords(row)}>
            Ack Records
          </button>
        </div>
      ),
      width: "150px",
    },
    {
      name: t("msg_reports.msg_dept_ack_status_report.col_size"),
      selector: (row) => row.sizeCode,
      sortable: true,
      width: "190px",
      cell: (row) => (
        <div>
          <p className="m-0 p-0 pt-1 fw-bold">{row.sizeCode}</p>
          <p className="m-0 p-0">{row.sizeName}</p>
        </div>
      ),
    },
    {
      name: t("msg_reports.msg_dept_ack_status_report.col_priority"),
      selector: (row) => row.priority,
      sortable: true,
      width: "160px",
      cell: (row) => (
        <>
          {row.priority === "urgent" ? (
            <span className="bg-table-status bg-table-danger-status">
              {t("msg_overview.urgent")}
            </span>
          ) : row.priority === "regular" ? (
            <span className="bg-table-status bg-table-info-status">
              <strong>{t("msg_overview.regular")}</strong>
            </span>
          ) : (
            <>{row.priority}</>
          )}
        </>
      ),
    },
    {
      name: t("msg_reports.msg_dept_ack_status_report.col_customer"),
      selector: (row) => row.customerName,
      sortable: true,
      width: "200px",
      cell: (row) => (
        <div>
          <p className="m-0 p-0 pt-1">{row.customerName}</p>
        </div>
      ),
    },
    {
      name: t("msg_reports.msg_production_status.req_date"),
      selector: (row) => row.requiredDate,
      sortable: true,
      width: "150px",
      cell: (row) => (
        <span>
          <p className="m-0 p-0 pt-1">{formatDate(row.requiredDate)}</p>
        </span>
      ),
    },
    {
      name: t("msg_reports.msg_dept_ack_status_report.col_plan"),
      selector: (row) => row.plan,
      sortable: true,
      width: "120px",
      cell: (row) => (
        <div>
          <p className="m-0 p-0 pt-1">{moment(row.plan).format("MMM-YYYY")}</p>
        </div>
      ),
    },
    {
      name: t("msg_reports.msg_dept_ack_status_report.col_planType"),
      selector: (row) => row.planType,
      sortable: true,
      width: "120px",
      cell: (row) => (
        <div>
          <p className="m-0 p-0 pt-1">{row.planType}</p>
        </div>
      ),
    },
    {
      name: t("msg_reports.msg_dept_ack_status_report.col_quantity"),
      selector: (row) => row.batchTotalQtyDozen,
      sortable: true,
      width: "150px",
      cell: (row) => (
        <span className="bg-table-status bg-table-text-status">
          {row.originalQuantityPerJobCard} / {row.quantityPerJobCard}
        </span>
      ),
    },
    {
      name: t("msg_reports.msg_dept_ack_status_report.col_jobcard_status"),
      selector: (row) => row.jobcardStatus,
      sortable: true,
      width: "200px",
      cell: (row) => (
        <>
          {row.jobcardStatus === "Inprogress" ? (
            <span className="bg-table-status bg-table-progress-status">
              {row.jobcardStatus}
            </span>
          ) : row.jobcardStatus === "Open" ? (
            <span className="bg-table-status bg-table-new-status">
              NOT Acknowledged
            </span>
          ) : row.jobcardStatus === "Stopped" ? (
            <span className="bg-table-status bg-table-danger-status">
              {row.jobcardStatus}
            </span>
          ) : row.jobcardStatus === "Acknowledged" ? (
            <span className="bg-table-status bg-table-success-status">
              {row.jobcardStatus}
            </span>
          ) : row.jobcardStatus === "Closed" ? (
            <span className="bg-table-status bg-table-info-status">
              {row.jobcardStatus}
            </span>
          ) : row.jobcardStatus === "Complete" ? (
            <span className="bg-table-status bg-table-documentNumber-status">
              {row.jobcardStatus}
            </span>
          ) : (
            <>{row.jobcardStatus}</>
          )}
        </>
      ),
    },
    {
      name: t("msg_reports.msg_dept_ack_status_report.col_fromDepartment"),
      selector: (row) => row.fromDeptCode,
      sortable: true,
      width: "250px",
      cell: (row) =>
        row.fromDeptCode ? (
          <span className="bg-table-status bg-table-first-status">
            {row.fromDeptCode} - {row.fromDeptName}
          </span>
        ) : (
          ""
        ),
    },
    {
      name: t("msg_reports.msg_dept_ack_status_report.col_toDepartment"),
      selector: (row) => row.toDeptCode,
      sortable: true,
      width: "250px",
      cell: (row) => (
        <span className="bg-table-status bg-table-second-status">
          {row.toDeptCode} - {row.toDeptName}
        </span>
      ),
    },
    {
      name: t("msg_reports.msg_dept_ack_status_report.col_teamlead"),
      selector: (row) => row.loginUserId,
      sortable: true,
      width: "200px",
      cell: (row) => (
        <div>
          <p className="m-0 p-0 pt-1">
            {row.loginUserId} - {row.loginUserName}
          </p>
        </div>
      ),
    },
    {
      name: t("msg_reports.msg_dept_ack_status_report.col_action"),
      selector: (row) => row.createdDate,
      sortable: true,
      width: "200px",
      cell: (row) => (
        <div>
          <p className="m-0 p-0 pt-1 fw-bold">
            {formatDateTime(row.createdDate)}
          </p>
        </div>
      ),
    },
  ];

  const handleDownload = async () => {
    if (!selectedDepartment || !requiredFrom) {
      toastFunc(
        Constants.MASTER.TOAST.error,
        "Please select Department and Date",
        3000,
      );
      return;
    }

    const obj = {
      deptCode: selectedDepartment?.id,
      currentDate: moment(requiredFrom).format("DD-MM-YYYY"),
      download: true,
    };

    try {
      await ItemService.getdeptAckStatusReports(obj);
      // ✅ No blob handling needed
    } catch (error) {
      console.error("Download error:", error);
      toastFunc(Constants.MASTER.TOAST.error, "Error downloading file", 3000);
    }
  };

  return (
    <main className="generatejobcard main" id="main">
      <Row className="justify-center subContent">
        <Col xs={12} md={12} className="rightSide">
          <div className="rightSideInner timesheet">
            <h2 className="mainTitle">
              {t(
                "msg_reports.msg_dept_ack_status_report.jobstatus_report_title",
              )}
            </h2>
            <p className="subText">
              {t("msg_reports.msg_dept_ack_status_report.details_page")}
            </p>
          </div>
        </Col>
        <hr />
      </Row>

      <Row className="mb-3 align-items-end">
        <Col className="px-3" xs={12} sm={12} md={5} lg={5}>
          <Form.Group className="mb-3" controlId="deptCode">
            <Form.Label className="col-form-label">
              {t("msg_master_list.msg_department.department_name")}
            </Form.Label>
            <DropdownListField
              id="deptCode"
              name="name"
              className="dropdownHive w-full rounded-md"
              data={allDepartmentMaster}
              onChange={handleDepartmentChange}
              placeholder="Please Select department"
              autoFocus={true}
              value={selectedDepartment}
            />
          </Form.Group>
        </Col>

        <Col className="px-3" xs={12} sm={5} md={3} lg={3}>
          <Form.Group className="mb-3" controlId="fromDate">
            <Form.Label className="col-form-label">Date</Form.Label>
            <DatePicker
              required
              max={new Date()}
              placeholder="Date"
              name="requiredFrom"
              valueFormat={{ dateStyle: "medium" }}
              value={requiredFrom}
              onChange={handleDateChange}
              autoFocus={false}
            />
          </Form.Group>
        </Col>

        <Col className="px-3 mb-3" xs={12} sm={2} md={2} lg={2}>
          <Button
            type="button"
            variant="primary"
            className="btn btn-primary ml-2"
            onClick={handleSearch}
            disabled={loaderStatus}
          >
            {loaderStatus ? "Loading..." : "Search"}
          </Button>

          <Button
            type="button"
            variant="secondary"
            className="btn btn-secondary ml-2"
            onClick={handleDownload}
          >
            <i className="bi bi-download" /> Export
          </Button>
        </Col>
      </Row>

      <div>
        <Card>
          <Card.Body>
            <DataTable
              selectableRowDisabled={false}
              selectableRows={false}
              columns={columnsData}
              data={rowsData}
              progressPending={loaderStatus}
              paginationRowsPerPageOptions={Constants.ROW_PER_PAGE}
              paginationPerPage={50}
              pagination
              paginationServer={false}
              highlightOnHover
              responsive
              noDataComponent="No records found"
            />
          </Card.Body>
        </Card>
      </div>

      {ackModel && (
        <AckRecords
          rowsData={ackData}
          show={ackModel}
          onHide={handleAckModelClose}
          jobcard={jobcard}
        />
      )}
    </main>
  );
};

export default DeptAckStatusReports;
